import { estaActiva } from "../services/cols/estado";

/* La unidad anterior y la siguiente del padrón.
 *
 * Lo pidió Igna mirando la ficha: *«estaría bueno tener unas flechitas arriba
 * que digan Anterior y Siguiente, una en cada lado, para ir al vehículo
 * siguiente o anterior»*. Hoy para pasar del 513 al 514 hay que volver al
 * buscador y tipear.
 *
 * Es puro —entra el padrón y el interno actual, salen dos números— así que
 * todo esto se prueba en una tabla.
 *
 * ── EL ORDEN ES NUMÉRICO Y NO ALFABÉTICO ─────────────────────────────────
 *
 * Es la misma trampa que ya está escrita en `listar_unidades` de FlotaBot:
 * alfabéticamente el **1000 va antes que el 999**, así que «siguiente» saltaría
 * de un lado al otro del padrón y las flechas se leerían como rotas. Y no
 * falla en ningún lado: la ficha que abre es una ficha real.
 *
 * ── LAS DADAS DE BAJA SE SALTEAN, Y SE DICE ──────────────────────────────
 *
 * De 658 tractores, **258 están dados de baja** — casi cuatro de cada diez.
 * Recorriéndolos, las flechas pasarían la mayor parte del tiempo en unidades
 * que ya no ruedan, y la función que se pidió —moverse por la flota— dejaría
 * de servir a la tercera vez.
 *
 * Pero saltear en silencio es recortar sin avisar, así que el botón lo dice en
 * su `title`. Y **la unidad en la que estás cuenta siempre**, esté de baja o
 * no: sin eso, abrir la ficha de una dada de baja te dejaría sin flechas —
 * varado justo donde llegaste desde el buscador, que es como se llega a una
 * unidad de baja.
 *
 * ── Y SE VUELVE AL PADRÓN DEL MISMO TIPO ─────────────────────────────────
 *
 * `430 internos existen en las dos tablas`, así que la lista sale de la
 * colección que corresponde y nunca se mezclan: desde el furgón 774 la
 * siguiente es el furgón 775, no el tractor.
 */

/* El interno como número, para ordenar. `null` si no se puede leer — una fila
 * sin interno no tiene lugar en una lista ordenada por interno, y colarla como
 * 0 la pondría primera. */
export function numeroDe(v) {
  const n = parseInt(String(v ?? "").replace(/\D/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

/* Los internos que las flechas recorren, ordenados.
 *
 * `actual` entra aunque esté de baja: es donde estás parado, y sin él no habría
 * desde dónde contar el anterior y el siguiente.
 */
export function recorrido(padron, actual = null) {
  const vistos = new Set();
  const out = [];
  for (const f of padron || []) {
    /* El padrón heredó de Firestore el interno en `id`; algunas vistas lo
     * llaman `interno`. Se miran los dos: con uno solo, media flota quedaría
     * fuera del recorrido sin que nada falle. */
    const n = numeroDe(f?.interno ?? f?.id);
    if (n === null || vistos.has(n)) continue;
    if (n !== actual && !estaActiva(f?.activo ?? f?.estado)) continue;
    vistos.add(n);
    out.push(n);
  }
  return out.sort((a, b) => a - b);
}

/* `{ anterior, siguiente }`, cada uno el interno o `null`.
 *
 * **En las puntas devuelve `null` y no da la vuelta.** Un recorrido circular
 * haría que apretar «Siguiente» en el último salte al primero, y quien lo
 * apretó creería que se le movió la pantalla sola. El botón se dibuja
 * deshabilitado, que dice que ahí se termina.
 */
export function vecinos(padron, interno) {
  const actual = numeroDe(interno);
  if (actual === null) return { anterior: null, siguiente: null };
  const lista = recorrido(padron, actual);
  const i = lista.indexOf(actual);
  /* Si la unidad no está en el padrón —pasa: portería y el taller van por
   * interno y hay internos sin fila— no se inventan vecinos. Elegir «el más
   * cercano» mandaría a una unidad que no tiene nada que ver con la que se
   * estaba mirando. */
  if (i === -1) return { anterior: null, siguiente: null };
  return {
    anterior: i > 0 ? lista[i - 1] : null,
    siguiente: i < lista.length - 1 ? lista[i + 1] : null,
  };
}
